/* eslint-disable @typescript-eslint/naming-convention */
import { AfterViewInit, Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort, SortDirection } from '@angular/material/sort';
import { AddressByGoogle, DTypeViewableCol, NextLocator } from '@grupo-constant/next-dtype/types';
import { TranslocoService } from '@ngneat/transloco';
import { compareAsc, differenceInMinutes, parseISO } from 'date-fns';
import L from 'leaflet';
import { cloneDeep } from 'lodash-es';
import { catchError, combineLatest, firstValueFrom, map, Subject, takeUntil, throwError } from 'rxjs';
import { LocaleString } from '../../../translate/localeString.model';
import { NextServicio } from '../../../../modules/admin/services/services.types';
import { NextTaskType, NextTaskTypeForm } from '../../../task-type/task-type.types';
import { ServicesService } from '../../../../modules/admin/services/services.service';
import { TasksService } from '../../../../modules/admin/tasks/tasks.service';
import { DialogsService } from '../../../../dialogs/dialogs.service';
import { LocalizePipe } from '../../../pipes/localize.pipe';
import { HttpService } from '../../../http/http.service';
import { MasterDataService } from '../../../master-data/master-data.service';
import { MessagesService } from '../../../../layout/common/messages/messages.service';
import { ServiceFormsService } from '../../../../modules/admin/service-forms/service-forms.service';
import { TaskTypeService } from '../../../task-type/task-type.service';
import { NextMethodsEnum } from '../../../enums/next-methods.enum';
import { CompanyDataUser, EventSummary, FormSummary, GeopositionSummary, Pagination, TaskOutputFile, UserDeviceInfo, ZombieNumberCompany, Task, TaskStatusEnum, TaskGoal } from '../../../../modules/admin/tasks/tasks.types';

@Component({
    selector: 'task-details-sidebar',
    templateUrl: './task-details-sidebar.component.html',
    styles: [
        /* language=SCSS */
        `
            .events-grid {
                grid-template-columns: 135px 135px 135px 135px 135px;

                @screen sm {
                    grid-template-columns: auto 100px 100px 100px 100px;
                }

                @screen md {
                    grid-template-columns: auto 120px 120px 120px 120px;
                }

                @screen lg {
                    grid-template-columns: auto 180px 180px 180px 180px;
                }
                @screen xl {
                    grid-template-columns: auto 180px 180px 180px 180px;
                }
                @screen 2xl {
                    grid-template-columns: auto 220px 220px 220px 220px;
                }
                @screen 3xl {
                    grid-template-columns: auto 250px 250px 250px 250px;
                }
            }

            .eventsMoreInfo-grid {
                grid-template-columns: 100px 100px 110px;

                @screen sm {
                    grid-template-columns: 150px 150px 150px;
                }

                @screen md {
                    grid-template-columns: 200px 200px 200px;
                }

                @screen lg {
                    grid-template-columns: 250px 250px 250px;
                }
                @screen xl {
                    grid-template-columns: 300px 300px 300px;
                }
                @screen 2xl {
                    grid-template-columns: 400px 400px 400px;
                }
                @screen 3xl {
                    grid-template-columns: 500px 500px 500px;
                }
            }

            ::ng-deep .my-custom-tooltip {
                max-width: 600px;
            }

            .leaflet-marker-icon.sans-serif {
                font-weight: bold;
                font-family: Sans Serif;
            }
        `
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TaskDetailsSidebarComponent implements OnInit, OnDestroy, AfterViewInit {

    @ViewChild('formsPaginator') formsPaginator: MatPaginator;
    @ViewChild('formsSort') formsSort: MatSort;
    @ViewChild('trackingPaginator') trackingPaginator: MatPaginator;
    @ViewChild('trackingSort') trackingSort: MatSort;
    @Output() readonly update: EventEmitter<void> = new EventEmitter<void>();
    @Output() readonly close: EventEmitter<void> = new EventEmitter<void>();

    task: Task;
    taskType: NextTaskType;
    service: NextServicio;
    address: AddressByGoogle;

    formFilterControl = new FormControl<string[]>([]);
    formFilter: Map<string, { formName: LocaleString; replies: number; deprecated: boolean }> = new Map();

    hideMessages: boolean = false;
    hideEvents: boolean = false;
    hideForms: boolean = false;

    taskEventSummary: EventSummary[] = [];
    taskTracking: EventSummary[] =  [];
    taskTrackingForView: EventSummary[] = [];
    trackingPagination: Pagination = { page: 0, startIndex: 0, endIndex: 0, lastPage: 0, length: 0, size: 5 };
    taskFormSummary: FormSummary[] = [];
    taskFormSummaryForView: FormSummary[] = [];
    taskOutputFile: TaskOutputFile[] = [];
    formPagination: Pagination = { page: 0, startIndex: 0, endIndex: 0, lastPage: 0, length: 0, size: 5 };
    lastUserDevice: UserDeviceInfo;
    taskGeopositionSummary: GeopositionSummary[];

    availableUsers: CompanyDataUser[];
    availableZombies: ZombieNumberCompany[];
    user: CompanyDataUser;
    zombie: ZombieNumberCompany;

    openedMessageInfoTooltip: EventSummary;

    // Map variables
    private marker: L.Marker;
    private marker1: L.Marker;
    private marker2: L.Marker;
    private marker3: L.Marker;
    private marker4: L.Marker;
    private icon = L.icon({
        iconUrl: 'assets/images/position-markers/position-marker-0.png',
        iconRetinaUrl: 'assets/images/position-markers/position-marker-0.png',
        iconSize: [45, 45], // size of the icon
        shadowSize: [32, 32], // size of the shadow
        iconAnchor: [10, 40], // point of the icon which will correspond to marker's location
        shadowAnchor: [2, 30],  // the same for the shadow
    });
    private map: L.Map = null;

    private initialized = new Subject<void>();
    private unsubscribeAll: Subject<void> = new Subject();


    constructor(
        private servicesService: ServicesService,
        private tasksService: TasksService,
        private dialogService: DialogsService,
        private localize: LocalizePipe,
        private http: HttpService,
        private _snackbar: MatSnackBar,
        private masterDataService: MasterDataService,
        private messagesService: MessagesService,
        private serviceFormsService: ServiceFormsService,
        private taskTypeService: TaskTypeService,
        private transloco: TranslocoService
    ) { }

    ngOnInit() {
        combineLatest([
            this.tasksService.availableUsers$,
            this.tasksService.availableZombies$
        ]).pipe(takeUntil(this.unsubscribeAll))
            .subscribe(async ([availableUsers, availableZombies]) => {
            this.availableUsers = availableUsers;
            this.availableZombies = availableZombies;
        });

        combineLatest([
            this.tasksService.taskById$,
            this.servicesService.service$
        ]).pipe(takeUntil(this.unsubscribeAll))
            .subscribe(async ([task, service]) => {
                this.hideEvents = false;
                this.hideMessages = false;
                this.service = service;
                this.address = null;
                this.task = task?.task;
                if (this.task) {
                    if (this.task?.serverPayload?.address) {
                        this.address = this.task.serverPayload.address;
                    }
                    if (this.task.taskTypeId) {
                        this.taskType = await firstValueFrom(this.taskTypeService.retrieveTaskTypeById(this.task.taskTypeId));
                    }
                    this.taskEventSummary = task?.taskEventSummary ?? [];
                    this.taskTracking = task?.taskTracking ?? [];
                    this.getTrackingForView();
                    this.taskFormSummary = task?.taskFormSummary ?? [];
                    this.taskOutputFile = task?.taskOutputFile;
                    if (this.taskType) {
                        for (const form of this.taskType.forms) {
                            if ((form.visiblePlanningAreaId.length === 0
                                || form.visiblePlanningAreaId.includes(this.task.serverPayload.planningAreaId))
                                && (form.visibleWorkDayTypeId.length === 0
                                    || form.visibleWorkDayTypeId.includes(this.task.serverPayload.schedule?.workDayType?._oid))) {
                                if (!form.deprecated || (form.deprecated && this.taskFormSummary.filter(f => f.formUri === form.formUri).length > 0)) {
                                    this.formFilter.set(form.formUri, {
                                        formName: form.caption,
                                        replies: this.taskFormSummary.filter(f => f.formUri === form.formUri).length,
                                        deprecated: form.deprecated ?? false
                                    });
                                }
                            }
                        }
                        this.formFilterControl.setValue(this.taskType.forms.map(f => f.formUri), { emitEvent: false });
                    }
                    this.getFormsForView();
                    this.lastUserDevice = task?.userLastDevice;
                    this.taskGeopositionSummary = task.taskGeopositionSummary;
                    this.user = this.availableUsers.find(u => u.userId === this.task?.userId);
                    this.zombie = this.availableZombies.find(z => z.planningArea === this.task.planningAreaId && z.number === this.task.zombieNumber);
                    this.initialized.next();
                    this.initialized.complete();
                    this.mounted();
                }
            });

        this.formFilterControl.valueChanges
            .pipe(takeUntil(this.unsubscribeAll))
            .subscribe(() => this.getFormsForView());
    }

    ngOnDestroy() {
        this.unsubscribeAll.next();
        this.unsubscribeAll.complete();
    }

    ngAfterViewInit(): void {
        this.initialized
            .pipe(takeUntil(this.unsubscribeAll))
            .subscribe({
                complete: () => {
                    if (this.taskGeopositionSummary) {
                        this.initMap(this.taskGeopositionSummary);
                    } else {
                        this.initMap();
                    }
                    if (this.formsPaginator && this.formsSort) {
                        this.formsSort.sortChange
                            .pipe(
                                takeUntil(this.unsubscribeAll),
                                map(() => {
                                    this.getFormsForView(this.formsPaginator.pageIndex, this.formsPaginator.pageSize, this.formsSort.active, this.formsSort.direction);
                                })
                            ).subscribe();
                        this.formsPaginator.page
                            .pipe(
                                takeUntil(this.unsubscribeAll),
                                map(() => {
                                    this.getFormsForView(this.formsPaginator.pageIndex, this.formsPaginator.pageSize, this.formsSort.active, this.formsSort.direction);
                                })
                            ).subscribe();
                    }
                    if (this.trackingPaginator && this.trackingSort) {
                        this.trackingSort.sortChange
                            .pipe(
                                takeUntil(this.unsubscribeAll),
                                map(() => {
                                    this.getTrackingForView(this.trackingPaginator.pageIndex, this.trackingPaginator.pageSize,
                                        this.trackingSort.active, this.trackingSort.direction);
                                })
                            ).subscribe();
                        this.trackingPaginator.page
                            .pipe(
                                takeUntil(this.unsubscribeAll),
                                map(() => {
                                    this.getTrackingForView(this.trackingPaginator.pageIndex, this.trackingPaginator.pageSize,
                                        this.trackingSort.active, this.trackingSort.direction);
                                })
                            ).subscribe();
                    }
                }
            });
    }

    mounted() {
        setTimeout(() => { window.dispatchEvent(new Event('resize')); }, 1000);
    };

    showMessagesToggle() {
        this.hideMessages = !this.hideMessages;
    }

    showEventsToggle() {
        this.hideEvents = !this.hideEvents;
    }

    showFormSummaryToggle() {
        this.hideForms = !this.hideForms;
    }

    printTime(str: string) {
        return str?.split(':').slice(0, 2).join(':');
    }

    async openTaskFormDialog(oid: string) {
        const taskType = await firstValueFrom(this.taskTypeService.retrieveTaskTypeById(this.task.taskTypeId));
        const formIndex = this.taskFormSummary.findIndex(t => t.oid === oid);
        const taskTypeForm = taskType.forms.find(f => f.formUri === this.taskFormSummary[formIndex].formUri);
        const locator: NextLocator = {
            serviceId: this.service.serviceId,
        };
        const readonly = this.task.status === TaskStatusEnum.WORKING || this.task.status === TaskStatusEnum.PAUSED;
        const taskFormDialog = this.dialogService.openTaskFormDialog(cloneDeep(this.taskFormSummary[formIndex]), taskTypeForm, this.task, locator, readonly);
        taskFormDialog.afterClosed().subscribe((formSummary: FormSummary) => {
            if (formSummary) {
                this.taskFormSummary[formIndex] = formSummary;
                this.getFormsForView(this.formsPaginator.pageIndex, this.formsPaginator.pageSize, this.formsSort.active, this.formsSort.direction);
            }
        });
    }

    async addNewForm() {
        const taskType = await firstValueFrom(this.taskTypeService.retrieveTaskTypeById(this.task.taskTypeId));
        const locator: NextLocator = {
            serviceId: this.service.serviceId,
        };
        const dialog = this.dialogService.openNewFormReplyDialog(this.task, taskType.forms);
        dialog.afterClosed().subscribe((form: NextTaskTypeForm) => {
            if (form) {
                const forms = this.taskFormSummary?.filter(f => f.formUri === form.formUri);
                if (form.maxOccurs && forms?.length >= form.maxOccurs) {
                    this.dialogService.openErrorDialog({
                        title: this.transloco.translate('cannotAddFormTitle'),
                        message: this.transloco.translate('cannotAddFormMsg')
                    });
                    return;
                }
                const taskFormDialog = this.dialogService.openTaskFormDialog({
                    formUri: form.formUri,
                    caption: form.caption,
                }, form, this.task, locator, false);
                taskFormDialog.afterClosed().subscribe((formSummary: FormSummary) => {
                    if (formSummary) {
                        this.taskFormSummary.push(formSummary);
                        this.formFilter.get(form.formUri).replies++;
                        this.getFormsForView(this.formsPaginator.pageIndex, this.formsPaginator.pageSize, this.formsSort.active, this.formsSort.direction);
                    }
                });
            }
        });
    }

    async openChangeLocationModal() {
        const reply = await firstValueFrom(this.masterDataService.retrieveLocationChangeRequest({
            includeMasterData: true,
            includeRequestData: true,
            oids: [this.task.addressOid]
        }));
        const item = reply.masterData[0];
        item.requestData = reply.requestData[0];
        const dialog = this.dialogService.openLocationChangeDialog(item);
        dialog.afterClosed().subscribe((result: 'approve' | 'reject') => {
            if (result !== undefined) {
                this.masterDataService.flushLocationChangeResult(this.task.addressOid, result === 'approve').pipe(
                    catchError((error) => {
                        console.error(error);
                        this.dialogService.openErrorDialog();
                        return throwError(() => error);
                    }),
                ).subscribe(() => {
                    this._snackbar.open(this.transloco.translate(result === 'approve' ? 'approveChangeLocationSuccess' : 'rejectChangeLocationSuccess'), this.transloco.translate('accept'), { duration: 5000 });
                    this.close.emit();
                });
            }
        });
    }

    sendPush() {
        this.messagesService.setMessagesFromTask(true);
        this.messagesService.openMessagePanel({ to: this.task.userId });
    }

    toggleMessage(event: EventSummary, $event) {
        $event.stopPropagation();
        if (this.openedMessageInfoTooltip) {
            this.openedMessageInfoTooltip.showInfo = false;
        }
        event.showInfo = !event.showInfo;
    }

    printTimeWithoutSeconds(time: string) {
        return time?.split(':').slice(0, 2).join(':');
    }

    printTimeFromMinutes(time: number) {
        if (time >= 0) {
            return '+' + this.minutesToString(time);
        } else {
            return '-' + this.minutesToString(Math.abs(time));
        }
    }

    printTimeFromTimestamp(time: string) {
        return time?.split('T')[1].split('.')[0].split(':').slice(0, 2).join(':');
    }

    differenceStartEndTime(task: Task) {
        if (task.status !== TaskStatusEnum.DONE) {
            return '';
        } else {
            if (!task.scheduled_from_time || !task.scheduled_to_time) {
                return '';
            }
            let diferenciaTiemposPlanned: number;
            let diferenciaTiemposDone: number;
            const fechaInicioDate = parseISO(task.scheduled_from_time);
            const fechaFinDate = parseISO(task.scheduled_to_time);
            const startTaskDate = new Date(task.startTaskDate);
            const endTaskDate = new Date(task.endTaskDate);
            if (task.workDayTypeMode !== 'SPLIT') {
                diferenciaTiemposPlanned = differenceInMinutes(fechaFinDate, fechaInicioDate, {roundingMethod: 'round'});
                diferenciaTiemposDone = differenceInMinutes(endTaskDate, startTaskDate, {roundingMethod: 'round'});
            } else {
                const fechaInicioDate2 = parseISO(task.scheduled_from_time2);
                const fechaFinDate2 = parseISO(task.scheduled_to_time2);
                const startTaskDate2 = new Date(task.startTaskDate2);
                const endTaskDate2 = new Date(task.endTaskDate2);
                diferenciaTiemposPlanned = differenceInMinutes(fechaFinDate, fechaInicioDate, {roundingMethod: 'round'}) +
                    differenceInMinutes(fechaFinDate2, fechaInicioDate2, {roundingMethod: 'round'});
                diferenciaTiemposDone = task.endTaskDate ? differenceInMinutes(endTaskDate, startTaskDate, {roundingMethod: 'round'}) +
                differenceInMinutes(endTaskDate2, startTaskDate2, {roundingMethod: 'round'})
                    : differenceInMinutes(endTaskDate2, startTaskDate2, {roundingMethod: 'round'});
            }
            const diferenciaTiempos = diferenciaTiemposDone - diferenciaTiemposPlanned;
            task.startEndDifferenceMinutes = diferenciaTiempos;
            if (diferenciaTiempos >= 0) {
                return '+' + this.minutesToString(diferenciaTiempos);
            } else {
                return '-' + this.minutesToString(Math.abs(diferenciaTiempos));
            }
        }
    }

    timeRemainingToStart(task: Task) {
        if (task.scheduled_from_time || task.scheduled_from_date) {
            const fechaInicioDate = parseISO(task.scheduled_from_time ?? task.scheduled_from_date);
            const today = new Date();
            const diferenciaTiempos = differenceInMinutes(today, fechaInicioDate, {roundingMethod: 'round'});
            task.timeRemainingToStart = diferenciaTiempos;
        } else {
            task.timeRemainingToStart = 0;
        }
    }

    timeRemainingToStart2(task: Task) {
        const fechaInicioDate2 = parseISO(task.scheduled_from_time2);
        const today = new Date();
        const diferenciaTiempos = differenceInMinutes(today, fechaInicioDate2, {roundingMethod: 'round'});
        task.timeRemainingToStart2 = diferenciaTiempos;
    }

    timeRemainingToEnd(task: Task) {
        const fechaFinDate = task.scheduled_to_time ? parseISO(task.scheduled_to_time) : undefined;
        const today = new Date();
        if (fechaFinDate) {
            const diferenciaTiempos = differenceInMinutes(fechaFinDate, today, {roundingMethod: 'round'});
            task.timeRemainingToEnd = diferenciaTiempos;
        } else {
            task.timeRemainingToEnd = 0;
        }
    }

    timeRemainingToEnd2(task: Task) {
        const fechaFinDate2 = parseISO(task.scheduled_to_time2);
        const today = new Date();
        const diferenciaTiempos = differenceInMinutes(fechaFinDate2, today, {roundingMethod: 'round'});
        task.timeRemainingToEnd2 = diferenciaTiempos;
    }

    printTimeRemaining(time: number) {
        time = Math.abs(time);
        const minutes = time % 60;
        let hours = Math.floor(time / 60);
        const days = hours >= 24 ? Math.floor(hours / 24) : 0;
        if (days > 0) {
            hours = hours - days * 24;
        }
        if (days > 0 && minutes > 0 && hours > 0) {
            return 'Quedan ' + days + 'd, ' + hours + 'h y ' + minutes + 'm';
        } else if (days > 0 && hours > 0) {
            return 'Quedan ' + days + 'd y ' + hours + 'h';
        } else if (days > 0 && minutes > 0) {
            return 'Quedan ' + days + 'd y ' + minutes + 'm';
        } else if (hours > 0 && minutes > 0) {
            return 'Quedan ' + hours + 'h y ' + minutes + 'm';
        } else if (days > 0) {
            return 'Quedan ' + days + 'd';
        } else if (hours > 0) {
            return 'Quedan ' + hours + 'h';
        } else if (minutes > 0) {
            return 'Quedan ' + minutes + 'm';
        }
    }

    workingTime(task: Task) {
        let difference: number = 0;
        if (task.workDayTypeMode !== 'SPLIT') {
            const startTaskDate = new Date(task.startTaskDate);
            let endTaskDate = new Date(task.endTaskDate);
            if (!task.endTaskDate) {
                endTaskDate = new Date();
            }
            difference = differenceInMinutes(endTaskDate, startTaskDate, { roundingMethod: 'round' });
        } else {
            if (task.startTaskDate && !task.endTaskDate) {
                difference += differenceInMinutes(new Date(), new Date(task.startTaskDate), { roundingMethod: 'round' });
            } else if (task.startTaskDate && task.endTaskDate) {
                difference += differenceInMinutes(new Date(task.endTaskDate), new Date(task.startTaskDate), { roundingMethod: 'round' });
                if (task.startTaskDate2 && task.endTaskDate2) {
                    difference += differenceInMinutes(new Date(task.endTaskDate2), new Date(task.startTaskDate2), { roundingMethod: 'round' });
                } else if (task.startTaskDate2 && !task.endTaskDate2) {
                    difference += differenceInMinutes(new Date(), new Date(task.startTaskDate2), { roundingMethod: 'round' });
                }
            }
        }
        task.workingTime = difference > 0 ? difference : 1;
        return this.minutesToString(difference);
    }

    getDate(str: string, task?: Task) {
        if (!task) {
            return new Date().toISOString().split('T')[0];
        } else {
            if (str === 'end') {
                return task.endTaskDate.split('T')[0];
            } else if (str === 'start') {
                return task.startTaskDate.split('T')[0];
            }
        }
    }

    reverseDate(date: string) {
        const year = date?.split('-')[0];
        const month = date?.split('-')[1];
        const day = date?.split('-')[2];
        return date ? day + '/' + month + '/' + year : '';
    }

    endedDelayed(task: Task) {
        const fechaFinDate = task.scheduled_to_time;
        const today = task.endTaskDate ? task.endTaskDate.split('T')[0] : new Date().toISOString().split('T')[0];
        return today > fechaFinDate;
    }

    getTaskAssignmentTimes(task: Task) {
        return this.reverseDate(task.scheduled_from_date) + ' ' + this.printTimeWithoutSeconds(task.scheduled_from_time) +
            this.reverseDate(task.scheduled_to_date) + ' ' + this.printTimeWithoutSeconds(task.scheduled_to_time);
    }

    indexImpar(index: number, task: Task) {
        if (task.status === 'DONE' || task.status === 'DONE_WITH_ERROR') {
            return index % 2 === 0;
        } else {
            return index % 2 !== 0;
        }
    }

    getTimeFromStartMobile(task: Task) {
        const today = new Date();
        const startTaskDate = new Date(task.startTaskDate);
        task.timeFromStart = Math.abs(differenceInMinutes(today, startTaskDate, {roundingMethod: 'round'}));
        return differenceInMinutes(today, startTaskDate, {roundingMethod: 'round'});
    }

    getStartTimeDiference(task: Task) {
        if (!task.scheduled_from_time) {
            return '';
        }
        const fechaInicioDate = parseISO(task.scheduled_from_time);
        if (task.status === 'WORKING' || task.status === 'DONE' || task.status === 'DONE_WITH_ERROR' || task.status === 'PAUSED') {
            if (!task.startTaskDate) {
                return '';
            }
            const startTaskDate = new Date(task.startTaskDate);
            return this.getDifferenceInString(startTaskDate, fechaInicioDate);
        } else if (task.status === 'SENT') {
            const today = new Date();
            return this.getDifferenceInString(today, fechaInicioDate);
        }
    }

    getEndTimeDiference(task: Task) {
        if (!task.scheduled_to_time) {
            return '';
        }
        const fechaFinDate = task.taskTypeTypology === 'WITHOUT_TIME' ? parseISO(task.scheduled_to_time) :
            parseISO(task.scheduled_to_time);
        if (task.status === 'DONE' || task.status === 'DONE_WITH_ERROR' || task.status === 'PAUSED') {
            if (!task.endTaskDate) {
                return '';
            }
            const endTaskDate = new Date(task.endTaskDate);
            return this.getDifferenceInString(endTaskDate, fechaFinDate);
        } else if (task.status=== 'WORKING' || (task.taskTypeTypology === 'WITHOUT_TIME' && (this.getTrackingEvent(task) === 'SENT' ||
            this.getTrackingEvent(task) === 'SENT_RC' || this.getTrackingEvent(task) === 'SENT_ACK'))) {
            if (task.endTaskDate) {
                const endTaskDate = new Date(task.endTaskDate);
                return this.getDifferenceInString(endTaskDate, fechaFinDate);
            } else {
                if (task.workDayTypeMode === 'SPLIT') {
                    return '';
                }
                const today = new Date();
                return this.getDifferenceInString(today, fechaFinDate);
            }
        }
    }

    getStartTimeDiference2(task: Task) {
        const fechaInicioDate = parseISO(task.scheduled_from_time2);
        if (task.status === 'WORKING' || task.status === 'DONE' || task.status === 'DONE_WITH_ERROR' || task.status === 'PAUSED') {
            if (!task.startTaskDate2) {
                return '';
            }
            const startTaskDate2 = new Date(task.startTaskDate2);
            return this.getDifferenceInString(startTaskDate2, fechaInicioDate);
        } else if (task.status=== 'SENT') {
            const today = new Date();
            return this.getDifferenceInString(today, fechaInicioDate);
        }
    }

    getEndTimeDiference2(task: Task) {
        const fechaFinDate = parseISO(task.scheduled_to_time2);
        if (task.status === 'DONE' || task.status === 'DONE_WITH_ERROR') {
            if (!task.endTaskDate2) {
                return '';
            }
            const endTaskDate2 = new Date(task.endTaskDate2);
            return this.getDifferenceInString(endTaskDate2, fechaFinDate);
        } else if (task.status === 'WORKING') {
            const today = new Date();
            return this.getDifferenceInString(today, fechaFinDate);
        }
    }

    minutesToString(minutes: number) {
        let hours = minutes >= 60 ? Math.floor(minutes / 60) : 0;
        const days = hours >= 24 ? Math.floor(hours / 24) : 0;
        if (days > 0) {
            hours = hours - days * 24;
        }
        let str = '';
        minutes = Math.round(minutes % 60);
        if (days > 0) {
            str = str + String(days) + 'd ';
        }
        if (hours > 0) {
            str = str + String(hours) + 'h ';
        }
        if (minutes > 0) {
            str = str + String(minutes) + 'm';
        }
        if (str === '') {
            return '0m';
        } else {
            return str;
        }
    }

    getTrackingEvent(task: Task) {
        switch (task.status) {
            case 'PLANNED':
            case 'WITHERROR':
            case 'CANCELLED':
            case 'REJECTED':
            case 'DONE':
            case 'DONE_WITH_ERROR':
                return task.status;
            case 'SENT':
                if (task.trackingEvent === 'SENT_ACK') {
                    return this.checkIfDelayed('SENT_ACK', task);
                } else if (task.trackingEvent === 'SENT_RC') {
                    return this.checkIfDelayed('SENT_ACK', task);
                } else if (task.trackingEvent === 'DELAYED_START') {
                    return 'DELAYED_START';
                } else if (task.trackingEvent === 'RESET') {
                    return this.checkIfDelayed('RESET', task);
                } else {
                    return this.checkIfDelayed('SENT', task);
                }
            case 'WORKING':
                if (task.trackingEvent === 'RESET') {
                    return this.checkIfDelayed('RESET', task);
                } else {
                    return this.checkIfDelayed(task.status, task);
                }
            case 'PAUSED':
                return this.checkIfDelayed('PAUSED', task);
        }
    }

    checkIfDelayed(event: string, task: Task) {
        if (event === 'SENT' || event === 'SENT_ACK' || event === 'SENT_RC') {
            if (task.status === 'WORKING') {
                return task.delayedEnd2 ? 'DELAYED_END2' : 'WORKING';
            } else {
                return task.delayedStart ? 'DELAYED_START' : event;
            }
        } else if (event === 'WORKING') {
            if (task.startTaskDate2) {
                return task.delayedEnd2 ? 'DELAYED_END2' : event;
            } else if (task.startTaskDate) {
                return task.delayedEnd ? 'DELAYED_END' : event;
            } else {
                return event;
            }
        } else if (event === 'PAUSED') {
            return task.delayedStart2 ? 'DELAYED_START2' : event;
        }
    }

    hasGeoposition(num: number) {
        switch(num) {
            case 1:
                return this.taskGeopositionSummary.some(g => g.event === 'START_TASK');
            case 2:
                return this.taskGeopositionSummary.some(g => g.event === 'END_TASK');
            case 3:
                return this.taskGeopositionSummary.some(g => g.event === 'START_TASK2');
            case 4:
                return this.taskGeopositionSummary.some(g => g.event === 'END_TASK2');
        }
    }

    goToMarker(num: number) {
        let geoposition: GeopositionSummary = {};
        switch(num) {
            case 0:
                if (this.task?.serverPayload?.address && this.task?.serverPayload?.address.latitude && this.task?.serverPayload?.address.longitude) {
                    geoposition.latitude = this.task?.serverPayload?.address.latitude;
                    geoposition.longitude = this.task?.serverPayload?.address.longitude;
                } else {
                    return;
                }
                break;
            case 1:
                geoposition =  this.taskGeopositionSummary.find(g => g.event === 'START_TASK');
                break;
            case 2:
                geoposition = this.taskGeopositionSummary.find(g => g.event === 'END_TASK');
                break;
            case 3:
                geoposition = this.taskGeopositionSummary.find(g => g.event === 'START_TASK2');
                break;
            case 4:
                geoposition = this.taskGeopositionSummary.find(g => g.event === 'END_TASK2');
                break;
        }
        this.centerMap(geoposition.latitude, geoposition.longitude);
    }

    canEditGoals() {
        return this.task && this.taskType?.goals?.length > 0 && ![TaskStatusEnum.CANCELLED, TaskStatusEnum.WORKING, TaskStatusEnum.REJECTED].includes(this.task.status);
    }

    async editGoals() {
        const taskGoals = await firstValueFrom(this.tasksService.retrieveTaskGoal(this.task.taskId, this.service.serviceId));
        if (this.taskType) {
            const dialog = this.dialogService.openTaskGoalDialog(this.taskType, this.task.scheduled_from_date, taskGoals);
            dialog.afterClosed()
                .pipe(takeUntil(this.unsubscribeAll))
                .subscribe(async (goals: TaskGoal[]) => {
                    if (goals) {
                        try {
                            const newGoalStatus = await firstValueFrom(this.tasksService.flushTaskGoal({
                                goals,
                                serviceId: this.service.serviceId,
                                taskId: this.task.taskId
                            }));
                            if (newGoalStatus) {
                                this.task.serverPayload.goalStatus = newGoalStatus;
                            }
                        } catch (error) {
                            console.error(error);
                            this.dialogService.openErrorDialog();
                        }
                    }
                });
        } else {
            this.dialogService.openErrorDialog({
                message: 'goal.noTaskTypeError'
            });
        }
    }

    hasExtraPayload() {
        return this.task?.serverPayload?.extraPayload ? Object.keys(this.task.serverPayload.extraPayload).length > 0 : false;
    }

    getExtraPayloadCaption(key: string) {
        const field = (this.taskType.dtype?.column ?? []).find(c => c.name === key);
        return field ? this.localize.transform((field as DTypeViewableCol).caption) ?? key : key;
    }

    async deleteForm(form: FormSummary) {
        const index = this.taskFormSummary.findIndex(f => f.oid === form.oid);
        if (index > -1) {
            const removedForms = this.taskFormSummary.splice(index, 1);
            this.getFormsForView(this.formsPaginator.pageIndex, this.formsPaginator.pageSize, this.formsSort.active, this.formsSort.direction);

            const countdownConfirm = await this.openConfirmDialogWithCountdown(
                this.transloco.translate('services.taskPlanning.confirmChangeMessage'),
                this.transloco.translate('services.taskPlanning.countdownMessage')
            );
            if (countdownConfirm) {
                try {
                    await firstValueFrom(this.serviceFormsService.deleteForm({ oid: form.oid, uri: form.formUri }));
                } catch (error) {
                    this.dialogService.openErrorDialog({
                        message: error?.error?.echo ? this.localize.transform(error?.error?.echo) : undefined,
                    });
                }
            } else {
                this.taskFormSummary.splice(index, 0, ...removedForms);
                this.getFormsForView(this.formsPaginator.pageIndex, this.formsPaginator.pageSize, this.formsSort.active, this.formsSort.direction);
            }
        }
    }

    async downloadTaskFile(outputFile: TaskOutputFile) {
        const response = await firstValueFrom(this.http.get(`${NextMethodsEnum.downloadFile}&fileName=${outputFile.docUrl}&asJson=true`));
        const base64 = response?.json;
        if (base64) {
            const e = document.createElement('a');
            e.download = outputFile.docUrl.substring(outputFile.docUrl.lastIndexOf('/'));
            e.href = base64;
            e.click();
        }
    }

    async executeTaskCustomAction(action: 'generate' | 'send') {
        try {
            await firstValueFrom(this.tasksService.executeTaskCustomAction({
                payload: {
                    action
                },
                serviceId: this.service.serviceId,
                taskId: this.task.taskId
            }));
            this._snackbar.open(this.transloco.translate('customActionSuccess'), this.transloco.translate('accept'), { duration: 5000 });
        } catch (error) {
            console.error(error);
            this.dialogService.openErrorDialog({
                message: error?.echo && Object.keys(error.echo).length ? this.localize.transform(error.echo) : undefined
            });
        }
    }

    private getTrackingForView(page: number = 0, size: number = 5, field = 'timestamp', dir: SortDirection = 'desc') {
        const trackingClone: EventSummary[] = this.sortTracking(field, dir);
        const begin = page * size;
        const end = Math.min((size * (page + 1)), trackingClone.length);
        const lastPage = Math.max(Math.ceil(trackingClone.length / size), 1);

        this.taskTrackingForView = trackingClone.slice(begin, end);
        this.trackingPagination = {
            length: trackingClone.length,
            size,
            page,
            lastPage,
            startIndex: begin,
            endIndex: end - 1
        };
    }

    private sortTracking(field: string, dir: SortDirection) {
        const trackingClone: EventSummary[] = structuredClone(this.taskTracking);
        return trackingClone.sort((a, b) => {
            if (field === 'event') {
                const compare = this.transloco.translate(a.event).localeCompare(this.transloco.translate(b.event));
                return dir === 'asc' ? compare : -compare;
            } else if (field === 'userName') {
                const compare = !a.userName ? -1 : !b.userName ? 1 : a.userName.localeCompare(b.userName);
                return dir === 'asc' ? compare : -compare;
            } else {
                const compare = compareAsc(new Date(a.timestamp), new Date(b.timestamp));
                return dir === 'asc' ? compare : -compare;
            }
        });
    }

    private getFormsForView(page: number = 0, size: number = 5, field = 'creationDate', dir: SortDirection = 'desc') {
        const formsClone: FormSummary[] = this.sortForms(field, dir);
        const begin = page * size;
        const end = Math.min((size * (page + 1)), formsClone.length);
        const lastPage = Math.max(Math.ceil(formsClone.length / size), 1);

        this.taskFormSummaryForView = formsClone.slice(begin, end);
        this.formPagination = {
            length: formsClone.length,
            size,
            page,
            lastPage,
            startIndex: begin,
            endIndex: end - 1
        };
    }

    private sortForms(field: string, dir: SortDirection) {
        const formsClone: FormSummary[] = structuredClone(this.taskFormSummary);
        return formsClone
            .filter(form => this.formFilterControl.value.includes(form.formUri))
            .sort((a, b) => {
                if (field === 'caption') {
                    const compare = this.localize.transform(a.caption).localeCompare(this.localize.transform(b.caption));
                    return dir === 'asc' ? compare : -compare;
                } else {
                    const compare = compareAsc(new Date(a[field]), new Date(b[field]));
                    return dir === 'asc' ? compare : -compare;
                }
            });
    }

    private initMap(geopositionSummary?: GeopositionSummary[]): void {
        //Create new map centered on Madrid
        if (!this.map) {
            this.map = L.map('mapId', {
                center: [40.416877, -3.703526],
                zoom: this.getZoom(),
                doubleClickZoom: false,
            });
        }

        const address = this.task?.serverPayload?.address;
        this.removeMarkers();
        if (geopositionSummary) {
            for (const geoposition of geopositionSummary) {
                this.addMarkers(geoposition);
            }
            this.centerMap(this.mediaLatitud(geopositionSummary), this.mediaLongitud(geopositionSummary));
        } else {
            if (address && address.latitude && address.longitude) {
                this.centerMap(address.latitude, address.longitude);
            }
        }
        if (address && address.latitude && address.longitude) {
            this.paintMarker(address.latitude, address.longitude);
        }


        //Set source of tiles
        const tiles = L.tileLayer(
            'https://maps.krama.es/hot/{z}/{x}/{y}.png',
            {
                maxZoom: 18,
                minZoom: 8,
                attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            });

        // Add tile to map
        tiles.addTo(this.map);
    }

    private getZoom() {
        const address = this.task?.serverPayload?.address;
        if (this.taskGeopositionSummary) {
            for (const geoposition1 of this.taskGeopositionSummary) {
                for (const geoposition2 of this.taskGeopositionSummary ) {
                    if (geoposition1.latitude > geoposition2.latitude && geoposition1.latitude - geoposition2.latitude > 0.015) {
                        return  14;
                    } else if (geoposition2.latitude > geoposition1.latitude && geoposition2.latitude - geoposition1.latitude > 0.015) {
                        return 14;
                    }
                    if (geoposition1.longitude > geoposition2.longitude && geoposition1.longitude - geoposition2.longitude > 0.015) {
                        return  14;
                    } else if (geoposition2.longitude > geoposition1.longitude && geoposition2.longitude - geoposition1.longitude > 0.015) {
                        return 14;
                    }
                }
                if (address) {
                    if (geoposition1.latitude > address.latitude && geoposition1.latitude - address.latitude > 0.015) {
                        return  14;
                    } else if (address.latitude > geoposition1.latitude && address.latitude - geoposition1.latitude > 0.015) {
                        return 14;
                    }
                    if (geoposition1.longitude > address.longitude && geoposition1.longitude - address.longitude > 0.015) {
                        return  14;
                    } else if (address.longitude > geoposition1.longitude && address.longitude - geoposition1.longitude > 0.015) {
                        return 14;
                    }
                }
            }
            return 15;
        } else {
            return 15;
        }
    }

    private mediaLatitud(geopositionSummary: GeopositionSummary[]) {
        let media: number = 0;
        for (const geoposition of geopositionSummary ) {
            media = media + geoposition.latitude;
        }
        media = media / geopositionSummary.length;
        return media;
    }

    private mediaLongitud(geopositionSummary: GeopositionSummary[]) {
        let media: number = 0;
        for (const geoposition of geopositionSummary ) {
            media = media + geoposition.longitude;
        }
        media = media / geopositionSummary.length;
        return media;
    }

    private removeMarkers() {
        if (this.marker1) {
            this.map.removeLayer(this.marker1);
        }
        if (this.marker2) {
            this.map.removeLayer(this.marker2);
        }
        if (this.marker3) {
            this.map.removeLayer(this.marker3);
        }
        if (this.marker4) {
            this.map.removeLayer(this.marker4);
        }
    }

    private addMarkers(geoposition: GeopositionSummary): void {
        let url: string;
        let icon: L.Icon;
        switch(geoposition.event) {
            case 'START_TASK':
                url = 'assets/images/position-markers/position-marker-1.png';
                icon = L.icon({
                    iconUrl: url,
                    iconRetinaUrl: url,
                    iconSize: [45, 45], // size of the icon
                    shadowSize: [32, 32], // size of the shadow
                    iconAnchor: [10, 40], // point of the icon which will correspond to marker's location
                    shadowAnchor: [2, 30],  // the same for the shadow
                });
                this.marker1 = L.marker(L.latLng(geoposition.latitude, geoposition.longitude), { icon: icon });
                this.map.addLayer(this.marker1);
                break;
            case 'END_TASK':
                url = 'assets/images/position-markers/position-marker-2.png';
                icon = L.icon({
                    iconUrl: url,
                    iconRetinaUrl: url,
                    iconSize: [45, 45], // size of the icon
                    shadowSize: [32, 32], // size of the shadow
                    iconAnchor: [10, 40], // point of the icon which will correspond to marker's location
                    shadowAnchor: [2, 30],  // the same for the shadow
                });
                this.marker2 = L.marker(L.latLng(geoposition.latitude, geoposition.longitude), { icon: icon });
                this.map.addLayer(this.marker2);
                break;
            case 'START_TASK2':
                url = 'assets/images/position-markers/position-marker-3.png';
                icon = L.icon({
                    iconUrl: url,
                    iconRetinaUrl: url,
                    iconSize: [45, 45], // size of the icon
                    shadowSize: [32, 32], // size of the shadow
                    iconAnchor: [10, 40], // point of the icon which will correspond to marker's location
                    shadowAnchor: [2, 30],  // the same for the shadow
                });
                this.marker3 = L.marker(L.latLng(geoposition.latitude, geoposition.longitude), { icon: icon });
                this.map.addLayer(this.marker3);
                break;
            case 'END_TASK2':
                url = 'assets/images/position-markers/position-marker-4.png';
                icon = L.icon({
                    iconUrl: url,
                    iconRetinaUrl: url,
                    iconSize: [45, 45], // size of the icon
                    shadowSize: [32, 32], // size of the shadow
                    iconAnchor: [10, 40], // point of the icon which will correspond to marker's location
                    shadowAnchor: [2, 30],  // the same for the shadow
                });
                this.marker4 = L.marker(L.latLng(geoposition.latitude, geoposition.longitude), { icon: icon });
                this.map.addLayer(this.marker4);
                break;
        }
    }

    private centerMap(lat: number, lng: number) {
        this.map.setView({ lat, lng }, this.map.getZoom(), { animate: true, duration: 1 });
    }

    private paintMarker(lat, lng) {
        if (this.marker) {
            this.map.removeLayer(this.marker);
        }
        this.marker = L.marker([lat, lng], { icon: this.icon });
        this.map.addLayer(this.marker);
    }

    private getDifferenceInString(date1: Date, date2: Date) {
        const diferenciaTiempos = differenceInMinutes(date1, date2, {roundingMethod: 'round'});
        if (diferenciaTiempos >= 0) {
            return '+' + this.minutesToString(diferenciaTiempos);
        } else {
            return '-' + this.minutesToString(Math.abs(diferenciaTiempos));
        }
    }

    private openConfirmDialogWithCountdown(message: string, countdownMessage: string) {
        const dialog = this.dialogService.openConfirmWithCountdownDialog({
            title: this.transloco.translate('warning'),
            message,
            countdownMessage,
            seconds: 10,
            actions: {
                confirm: {
                    label: this.transloco.translate('confirm')
                },
                cancel: {
                    label: this.transloco.translate('revert'),
                }
            },
        });

        return new Promise<boolean>((resolve) => {
            dialog.afterClosed().subscribe((result) => {
                if (result === 'confirmed') {
                    resolve(true);
                } else {
                    resolve(false);
                }
            });
        });
    }
}

